import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { EVENT_PRICING } from '../utils/pricing';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reviews = () => {
  const { isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await axios.get(`${API}/reviews`);
        setReviews(response.data);
      } catch (error) {
        console.error('Error fetching reviews:', error);
        // Fallback sample reviews
        setReviews([
          {
            id: '1',
            user_name: 'Sarah Mitchell',
            rating: 5,
            comment: 'Joywin captured our wedding day beautifully! Every photo tells a story. The attention to detail was incredible, and we received our photos much faster than expected. Highly recommend to anyone looking for a professional photographer!',
            event_type: 'civil_wedding',
            created_at: '2024-01-15'
          },
          {
            id: '2',
            user_name: 'James Thompson',
            rating: 5,
            comment: 'Amazing work on our daughter\'s baptism photos. The quality exceeded our expectations. Joywin made everyone feel comfortable and captured genuine moments we\'ll treasure forever.',
            event_type: 'baptism',
            created_at: '2024-01-10'
          },
          {
            id: '3',
            user_name: 'Emily Roberts',
            rating: 5,
            comment: 'The team was so professional and friendly. Our 50th anniversary photos are absolute treasures! The photo book we received was stunning quality.',
            event_type: 'silver_golden_wedding',
            created_at: '2024-01-05'
          },
          {
            id: '4',
            user_name: 'Michael Chen',
            rating: 5,
            comment: 'Booked for my 21st birthday party and couldn\'t be happier. The photos captured the energy and joy of the night perfectly. Great value for money!',
            event_type: '21st_birthday',
            created_at: '2023-12-20'
          },
          {
            id: '5',
            user_name: 'Lisa Anderson',
            rating: 4,
            comment: 'Very professional service for our baby\'s first birthday. The photos are beautiful and the editing is top-notch. Would definitely book again.',
            event_type: 'first_birthday',
            created_at: '2023-12-15'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${i < rating ? 'text-[#D4AF37] fill-[#D4AF37]' : 'text-[#52525B]'}`}
      />
    ));
  };

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] pt-24 flex items-center justify-center">
        <div className="animate-pulse text-[#A1A1AA]">Loading reviews...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-24 pb-16" data-testid="reviews-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <span className="text-[#D4AF37] tracking-[0.3em] text-sm uppercase">Testimonials</span>
          <h1 className="font-['Playfair_Display'] text-4xl md:text-5xl text-[#EDEDED] mt-4">
            Client <span className="gold-gradient-text">Reviews</span>
          </h1>
          
          {/* Stats */}
          <div className="flex items-center justify-center gap-8 mt-8">
            <div className="text-center">
              <div className="flex items-center justify-center gap-1 mb-2">
                {renderStars(Math.round(averageRating))}
              </div>
              <p className="text-3xl font-['Playfair_Display'] text-[#D4AF37]">{averageRating}</p>
              <p className="text-[#52525B] text-sm">Average Rating</p>
            </div>
            <div className="w-px h-16 bg-white/10"></div>
            <div className="text-center">
              <p className="text-3xl font-['Playfair_Display'] text-[#EDEDED]">{reviews.length}</p>
              <p className="text-[#52525B] text-sm">Total Reviews</p>
            </div>
          </div>
        </motion.div>

        {/* Write Review CTA */}
        {isAuthenticated ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12"
          >
            <Link to="/dashboard">
              <Button className="gold-gradient-btn" data-testid="write-review-btn">
                Write a Review
              </Button>
            </Link>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center mb-12"
          >
            <p className="text-[#A1A1AA] mb-4">Had a session with us? Share your experience!</p>
            <Link to="/login?redirect=/dashboard">
              <Button variant="outline" className="border-[#D4AF37] text-[#D4AF37]" data-testid="login-to-review-btn">
                Login to Write a Review
              </Button>
            </Link>
          </motion.div>
        )}

        {/* Reviews Grid */}
        <div className="grid md:grid-cols-2 gap-8" data-testid="reviews-list">
          {reviews.map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-[#0a0a0a] rounded-xl p-8 border border-white/10 relative"
              data-testid={`review-item-${index}`}
            >
              <Quote className="absolute top-6 right-6 w-10 h-10 text-[#D4AF37]/10" />
              
              {/* Stars */}
              <div className="flex gap-1 mb-4">
                {renderStars(review.rating)}
              </div>

              {/* Comment */}
              <p className="text-[#A1A1AA] mb-6 leading-relaxed text-lg">
                "{review.comment}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#997B1E] flex items-center justify-center">
                    <span className="text-black font-semibold">
                      {review.user_name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="text-[#EDEDED] font-medium">{review.user_name}</p>
                    {review.event_type && (
                      <p className="text-[#52525B] text-sm">
                        {EVENT_PRICING[review.event_type]?.name || review.event_type.replace(/_/g, ' ')}
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-[#52525B] text-sm">
                  {new Date(review.created_at).toLocaleDateString('en-GB', {
                    month: 'short',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Empty State */}
        {reviews.length === 0 && (
          <div className="text-center py-16">
            <p className="text-[#A1A1AA] text-lg">No reviews yet. Be the first to share your experience!</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Reviews;
